import { NgClass } from '@angular/common';
import { Component, computed, input, model, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';

/**
 * A generation model the skill creator can pick from. Mirrors the shape of the
 * `AvailableModelView` the catalog endpoint returns (SB-3318): the catalog owns
 * the order (provisioning order) and the default flag — this UI never sorts or
 * re-derives them. `displayName` is a product name (e.g. "GPT-5.4") shown
 * verbatim and is deliberately not translated.
 *
 * Kept as a local interface (identical in shape to the generated
 * `AvailableModelView`) so the component stays decoupled from the generated
 * client — the page maps `AvailableModelView` → `ModelOption` at the load
 * boundary rather than feeding the generated type straight in.
 */
export interface ModelOption {
  slug: string;
  displayName: string;
  isDefault: boolean;
}

let nextUniqueId = 0;

/**
 * The Model dropdown in the Advanced Skill Builder — a single-select control,
 * cloned from the Type selector, that sits to the right of it. Options are
 * pushed in by the page (loaded from the catalog endpoint); the control is
 * locked on a saved skill via `disabled`.
 */
@Component({
  selector: 'squadbox-assistant-builder-model-selector',
  imports: [
    TranslateModule,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    NgClass,
  ],
  template: `
    <!-- The row wrapper (flex gap-6 py-6) and this column's width (the Model
         selector grows to fill; Type is a fixed w-[340px]) are applied by the
         parent page (assistant-builder.page.ts) — do not add them here. -->
    <div class="flex flex-col gap-2">
      <label
        [for]="buttonId"
        class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5"
        [ngClass]="{
          'text-neutral-400': disabled(),
          'text-neutral-700': !disabled(),
        }"
      >
        {{
          'ASSISTANTS.ASSISTANT_BUILDER.MODEL_SELECTOR.DROPDOWN_LABEL'
            | translate
        }}
        <span class="text-primary-500"> *</span>
      </label>
      <button
        [id]="buttonId"
        class="
          group
          flex flex-row gap-2 items-center
          bg-shades-white rounded-lg px-3 py-2 h-12
          border border-neutral-200 hover:border-primary-300 focus:outline-hidden
          disabled:bg-neutral-100 disabled:border-neutral-200 disabled:hover:border-neutral-200 disabled:pointer-events-none
        "
        [sdDropdownMenuTrigger]="modelDropdown"
        [disabled]="disabled()"
      >
        <span
          class="typo-p3 group-disabled:text-neutral-400 text-left grow min-w-0 truncate"
          >{{ selectedLabel() }}</span
        >
        <svg
          [sdUseArrowIcon]="
            modelDropdownMenuOpened()
              ? 'chevron-small-up'
              : 'chevron-small-down'
          "
          class="size-6 shrink-0 group-disabled:text-neutral-400"
        ></svg>
      </button>
    </div>

    <sd-dropdown-menu
      #modelDropdown
      width="trigger"
      [padded]="true"
      [options]="modelDropdownMenuOptions()"
      (opened)="modelDropdownMenuOpened.set(true)"
      (closed)="modelDropdownMenuOpened.set(false)"
    />
  `,
})
export class AssistantBuilderModelSelectorComponent {
  protected readonly buttonId = `assistant-builder-model-selector-button-${nextUniqueId++}`;

  public readonly disabled = input<boolean>(false);
  public readonly options = input<ModelOption[]>([]);
  public readonly selectedSlug = model.required<string>();

  public readonly modelDropdownMenuOpened = signal(false);

  /**
   * The text shown in the (closed) trigger: the selected model's display name,
   * or the raw slug when the stored slug is no longer in the catalog (a model
   * removed after creation — shown read-only). Empty when nothing is selected
   * yet (e.g. before the catalog has loaded).
   */
  public readonly selectedLabel = computed((): string => {
    const slug = this.selectedSlug();
    return this.options().find((m) => m.slug === slug)?.displayName ?? slug;
  });

  /**
   * Dropdown options in catalog (provisioning) order — NO client-side sort; the
   * catalog endpoint owns the order.
   *
   * `type` carries the slug: it is the option's identity AND the discriminator
   * `sd-dropdown-menu` requires to render single-select semantics — radio role +
   * `aria-checked` on desktop and the selected-row highlight in the mobile
   * drawer (both gate on a defined `type`). Mirrors the Type selector.
   */
  public readonly modelDropdownMenuOptions = computed(
    (): fromSeriousSystem.DropdownMenuOption[] =>
      this.options().map((option) => ({
        type: option.slug,
        label: option.displayName,
        active: option.slug === this.selectedSlug(),
        action: () => this.selectedSlug.set(option.slug),
      })),
  );
}
